import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  robots?: string;
  noindex?: boolean;
}

/**
 * Custom hook to set page-specific SEO metadata
 */
export const useSEO = ({ title, description, keywords, ogImage, canonical, robots, noindex }: SEOProps) => {
  const location = useLocation();

  useEffect(() => {
    // Set page title
    document.title = title;

    // Set or update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', description);
      document.head.appendChild(metaDescription);
    }

    // Set or update meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      } else {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        metaKeywords.setAttribute('content', keywords);
        document.head.appendChild(metaKeywords);
      }
    }

    // Set Open Graph title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }

    // Set Open Graph description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }

    // Set Open Graph URL
    let ogUrl = document.querySelector('meta[property="og:url"]');
    const currentUrl = `https://marketnest.org${location.pathname}`;
    if (ogUrl) {
      ogUrl.setAttribute('content', currentUrl);
    }

    // Set Open Graph image if provided
    if (ogImage) {
      let ogImageMeta = document.querySelector('meta[property="og:image"]');
      if (ogImageMeta) {
        ogImageMeta.setAttribute('content', ogImage);
      }
    }

    // Set Twitter Card title
    let twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }

    // Set Twitter Card description
    let twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }

    // Set Twitter Card URL
    let twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', currentUrl);
    }

    // Set canonical URL
    const canonicalUrl = canonical || currentUrl;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute('href', canonicalUrl);
    } else {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      linkCanonical.setAttribute('href', canonicalUrl);
      document.head.appendChild(linkCanonical);
    }

    // Set robots meta tag
    const robotsContent = noindex ? 'noindex, nofollow' : (robots || 'index, follow');
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', robotsContent);
    } else {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      metaRobots.setAttribute('content', robotsContent);
      document.head.appendChild(metaRobots);
    }
  }, [title, description, keywords, ogImage, canonical, robots, noindex, location.pathname]);
};

/**
 * Generate structured data for LocalBusiness
 */
export const getLocalBusinessSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://marketnest.org/#organization',
    name: 'Market Nest',
    description: 'Data-driven digital marketing agency in Greensboro, NC specializing in SEO, PPC, social media marketing, and analytics-driven campaigns.',
    url: 'https://marketnest.org',
    telephone: '+1-919-504-1542',
    email: 'info@marketnest.org',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Greensboro',
      addressLocality: 'Greensboro',
      addressRegion: 'NC',
      postalCode: '27401',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 36.0726,
      longitude: -79.7920
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 36.0726,
        longitude: -79.7920
      },
      geoRadius: '50000' // 50km radius
    },
    priceRange: '$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00'
      }
    ],
    sameAs: [
      'https://www.facebook.com/marketnest', // TODO: Add actual social media URLs
      'https://www.linkedin.com/company/marketnest',
      'https://twitter.com/marketnest'
    ]
  };
};

/**
 * Generate structured data for Organization
 */
export const getOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Market Nest',
    alternateName: 'Market Nest Digital Marketing',
    url: 'https://marketnest.org',
    logo: 'https://marketnest.org/logo.svg',
    description: 'Premier digital marketing agency in Greensboro, NC offering SEO, PPC, social media marketing, content strategy, and analytics services.',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-919-504-1542',
      contactType: 'customer service',
      areaServed: 'US',
      availableLanguage: 'English'
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Greensboro',
      addressRegion: 'NC',
      addressCountry: 'US'
    }
  };
};

/**
 * Generate structured data for Service
 */
export const getServiceSchema = (serviceName: string, description: string, url: string) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: serviceName,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Market Nest',
      url: 'https://marketnest.org'
    },
    description: description,
    url: url,
    areaServed: {
      '@type': 'State',
      name: 'North Carolina'
    }
  };
};

/**
 * Generate structured data for BreadcrumbList
 */
export const getBreadcrumbSchema = (items: { name: string; url: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};

/**
 * Component to inject structured data into page
 */
export const StructuredData: React.FC<{ data: any }> = ({ data }) => {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};
